// What is actually in this order.
//
// Both order views showed the client and nothing about the cable, so ten
// orders for the same customer were ten identical rows — you opened each one
// to find the one you wanted. The lines are right there on the order; nobody
// had put them on the row.
//
// Named with `cableLabel`, which is the only naming this app should use: a
// 3 x 25 + 16 and a 3 x 25 are different cables with different bills of
// material, and the green-yellow section is part of the name.

import { cableLabel } from './cableLabel.js';

const num = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * One entry per distinct cable on the order, biggest first.
 *
 * Lines are merged by label: an order with three lines of the same cable is
 * one cable, and showing it three times is the noise this is meant to remove.
 * Cancelled lines are left out — they are not what the order is for.
 *
 * @returns [{ label, metres, lines }]
 */
export function orderProducts(order) {
  const lines = Array.isArray(order?.lines) ? order.lines : [];
  const byLabel = new Map();

  for (const line of lines) {
    if (String(line?.status || '').toUpperCase() === 'CANCELLED') continue;
    const label = cableLabel(line?.product, line?.productKey || '');
    if (!label) continue;

    const metres = num(line?.totalOrderedLength);
    const prev = byLabel.get(label);
    if (prev) {
      prev.metres += metres;
      prev.lines += 1;
    } else {
      byLabel.set(label, { label, metres, lines: 1 });
    }
  }

  return [...byLabel.values()].sort((a, b) => b.metres - a.metres);
}

/**
 * The same thing as one line of text, for a narrow column or a search index.
 *
 * @param max how many to name before summarising the rest. A row that wraps
 *            to four lines costs more than the detail is worth.
 */
export function orderProductsSummary(order, max = 2) {
  const products = orderProducts(order);
  if (!products.length) return '';

  const shown = products.slice(0, max).map(p => p.label);
  const rest = products.length - shown.length;
  return rest > 0 ? `${shown.join(', ')} +${rest} more` : shown.join(', ');
}

/** Everything searchable about an order's contents, lowercased. */
export const orderProductsSearchText = order =>
  orderProducts(order)
    .map(p => p.label)
    .join(' ')
    .toLowerCase();

/** One delivery-plan item, described the way the floor says it. */
export function describePlanItem(it) {
  const kind = String(it?.kind || '').toUpperCase();
  const qty = num(it?.qtyPlanned);
  const len = num(it?.lengthPerUnit);

  if (kind === 'DRUM') {
    const mat = String(it?.drumMaterial || '').trim();
    const dia = num(it?.drumDiameter);
    // Material and diameter are what an operator goes looking for in the
    // yard; "3 x drum" names nothing they can fetch.
    const spec = [mat, dia ? `Ø${dia}` : ''].filter(Boolean).join(' ');
    return { kind, qty, len, spec: spec || 'drum', produced: num(it?.qtyProduced) };
  }
  return { kind: kind || 'COIL', qty, len, spec: 'coil', produced: num(it?.qtyProduced) };
}

/**
 * Per LINE, not per cable: everything the order monitor shows about progress,
 * plus the delivery plan the line is to be made as.
 *
 * Merging by cable is right for a one-glance count and wrong here — two lines
 * of the same cable can be a drum line and a coil line, due on different days,
 * and rolling them together hides the only thing worth opening the panel for.
 *
 * The arithmetic is deliberately the same as OrdersMonitorTable's: a second
 * way of computing "remaining" is a second number for the same question.
 */
export function orderLineDetails(order) {
  const lines = Array.isArray(order?.lines) ? order.lines : [];

  return lines
    .filter(l => l && String(l?.status || '').toUpperCase() !== 'CANCELLED')
    .map(line => {
      const ordered = num(line.totalOrderedLength);
      const produced = num(line.producedLength);
      const allocated = num(line.allocatedLength);
      const fromStock = num(line.stockReservedLength);
      // Allocated that has not been made yet — counting it and `produced`
      // separately against the total would subtract the same metres twice.
      const allocNotProd = Math.max(0, allocated - produced);
      const remaining = Math.max(0, ordered - produced - fromStock - allocNotProd);

      return {
        lineId: line.lineId,
        label: cableLabel(line?.product, line?.productKey || ''),
        ordered,
        produced,
        allocated,
        fromStock,
        remaining,
        dueDate: line.dueDate || null,
        items: (Array.isArray(line?.deliveryPlan?.items) ? line.deliveryPlan.items : [])
          .map(describePlanItem)
          .filter(i => i.qty > 0),
      };
    });
}
